import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  CardBody,
  Spinner,
  Button,
  Select,
  SelectItem,
  Chip,
} from '@nextui-org/react';
import {
  BarChart3,
  Trophy,
  Award,
  Medal,
  AlertTriangle,
  RefreshCw,
  Info,
  ArrowUp,
  ArrowDown,
  Minus,
  BookOpen,
  Crown,
  ExternalLink,
  HelpCircle,
  Sparkles,
} from 'lucide-react';
import proRankingService, {
  ProRanking,
  ProRankingEntry,
  ProRankingEntriesResponse,
} from '../services/proRankingService';

const TEAL = '#3FCED2';
const PAGE_SIZE = 100;

const RankingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug?: string }>();

  const [rankings, setRankings] = useState<ProRanking[]>([]);
  const [loadingRankings, setLoadingRankings] = useState(true);
  const [rankingsError, setRankingsError] = useState<string | null>(null);

  const [selectedRankingId, setSelectedRankingId] = useState<string | null>(null);
  const [entriesResponse, setEntriesResponse] = useState<ProRankingEntriesResponse | null>(null);
  const [allEntries, setAllEntries] = useState<ProRankingEntry[]>([]);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const selectedRanking = useMemo(
    () => rankings.find((r) => (r.id || r._id) === selectedRankingId) || null,
    [rankings, selectedRankingId],
  );

  const fetchRankings = useCallback(async () => {
    try {
      setLoadingRankings(true);
      setRankingsError(null);
      const data = await proRankingService.getAllRankings();
      const sorted = [...data].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
      setRankings(sorted);
    } catch (err) {
      console.error('Fehler beim Laden der Ranglisten:', err);
      setRankingsError('Ranglisten konnten nicht geladen werden');
    } finally {
      setLoadingRankings(false);
    }
  }, []);

  useEffect(() => {
    fetchRankings();
  }, [fetchRankings]);

  const selectRanking = useCallback(async (rankingId: string, updateUrl: boolean = true) => {
    setSelectedRankingId(rankingId);
    setLoadingEntries(true);
    setAllEntries([]);
    setEntriesResponse(null);

    try {
      const response = await proRankingService.getRankingEntries(rankingId, PAGE_SIZE, 0);
      setEntriesResponse(response);
      setAllEntries(response.entries);

      if (updateUrl) {
        const ranking = rankings.find((r) => (r.id || r._id) === rankingId);
        if (ranking?.slug) {
          window.history.replaceState(null, '', `/ranglisten/${ranking.slug}`);
        }
      }
    } catch (err) {
      console.error('Fehler beim Laden der Einträge:', err);
    } finally {
      setLoadingEntries(false);
    }
  }, [rankings]);

  // Initial selection (by slug if available, else first ranking)
  useEffect(() => {
    if (rankings.length === 0 || selectedRankingId) return;

    if (slug) {
      const found = rankings.find((r) => r.slug === slug);
      if (found && (found.id || found._id)) {
        selectRanking((found.id || found._id) as string, false);
        return;
      }
    }

    const first = rankings[0];
    const firstId = first.id || first._id;
    if (firstId) selectRanking(firstId, true);
  }, [rankings, slug, selectedRankingId, selectRanking]);

  const loadMoreEntries = useCallback(async () => {
    if (loadingMore || !selectedRankingId) return;
    if (!entriesResponse?.pagination?.hasMore) return;

    setLoadingMore(true);
    try {
      const response = await proRankingService.getRankingEntries(
        selectedRankingId,
        PAGE_SIZE,
        allEntries.length,
      );
      setAllEntries((prev) => [...prev, ...response.entries]);
      setEntriesResponse((prev) =>
        prev
          ? { ...prev, pagination: response.pagination, ranking: response.ranking }
          : response,
      );
    } catch (err) {
      console.error('Fehler beim Nachladen:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [allEntries.length, entriesResponse, loadingMore, selectedRankingId]);

  const remainingEntries = useMemo(() => {
    const total = entriesResponse?.pagination?.total ?? 0;
    return Math.max(0, total - allEntries.length);
  }, [entriesResponse, allEntries.length]);

  // ---------- Loading / Error states ----------
  if (loadingRankings) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-20">
        <Helmet>
          <title>Ranglisten | DartCloud</title>
        </Helmet>
        <div className="flex justify-center items-center h-64">
          <Spinner size="lg" style={{ color: TEAL }} />
        </div>
      </div>
    );
  }

  if (rankingsError) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-20">
        <Helmet>
          <title>Ranglisten | DartCloud</title>
        </Helmet>
        <div className="max-w-7xl mx-auto px-4 py-12 text-center">
          <AlertTriangle size={64} className="mx-auto text-gray-500 mb-4" />
          <p className="text-gray-400 mb-4">{rankingsError}</p>
          <Button
            color="primary"
            startContent={<RefreshCw size={16} />}
            onPress={fetchRankings}
            style={{ backgroundColor: TEAL, color: '#000' }}
          >
            Erneut versuchen
          </Button>
        </div>
      </div>
    );
  }

  if (rankings.length === 0) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-20">
        <Helmet>
          <title>Ranglisten | DartCloud</title>
        </Helmet>
        <div className="max-w-7xl mx-auto px-4 py-12 text-center">
          <BarChart3 size={64} className="mx-auto text-gray-600 mb-4" />
          <h3 className="text-xl font-semibold text-gray-400 mb-2">Keine Ranglisten verfügbar</h3>
          <p className="text-gray-500">Es wurden noch keine Ranglisten konfiguriert.</p>
        </div>
      </div>
    );
  }

  const pageTitle = selectedRanking?.seoTitle
    || (selectedRanking?.name ? `${selectedRanking.name} | Ranglisten | DartCloud` : 'Ranglisten | DartCloud');
  const pageDescription = selectedRanking?.seoDescription
    || selectedRanking?.intro
    || 'Aktuelle Dart-Ranglisten auf DartCloud. PDC Order of Merit, Weltranglisten und weitere Verbands-Rankings.';

  return (
    <div className="min-h-screen bg-zinc-950 pt-20">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link
          rel="canonical"
          href={selectedRanking?.slug
            ? `https://dartcloudapp.de/ranglisten/${selectedRanking.slug}`
            : 'https://dartcloudapp.de/ranglisten'}
        />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
            <BarChart3 size={40} style={{ color: TEAL }} />
            Ranglisten
          </h1>
          <p className="text-gray-400">
            Aktuelle Profi-Ranglisten der PDC, WDF und weiterer Organisationen
          </p>
        </div>

        {/* Mobile: Ranking selector */}
        <div className="block lg:hidden mb-6">
          <Select
            label="Rangliste"
            placeholder="Rangliste wählen"
            selectedKeys={selectedRankingId ? [selectedRankingId] : []}
            onChange={(e) => {
              const id = e.target.value;
              if (id) selectRanking(id);
            }}
            classNames={{
              trigger: 'bg-zinc-900 border-zinc-800',
              listbox: 'bg-zinc-900',
            }}
          >
            {rankings.map((r) => {
              const id = (r.id || r._id) as string;
              return (
                <SelectItem key={id} value={id}>
                  {r.name || r.shortName || 'Rangliste'}
                </SelectItem>
              );
            })}
          </Select>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Main content - left (desktop) */}
          <div className="lg:col-span-9 order-2 lg:order-1">
            {selectedRanking && (
              <RankingHero ranking={selectedRanking} />
            )}

            {selectedRanking && <RankingKeyFacts ranking={selectedRanking} />}

            {loadingEntries ? (
              <div className="flex justify-center items-center h-64">
                <Spinner size="lg" style={{ color: TEAL }} />
              </div>
            ) : (
              <EntriesTable
                ranking={entriesResponse?.ranking || null}
                entries={allEntries}
                onLoadMore={loadMoreEntries}
                hasMore={entriesResponse?.pagination?.hasMore ?? false}
                loadingMore={loadingMore}
                remaining={remainingEntries}
              />
            )}

            {selectedRanking && <RankingInfoSections ranking={selectedRanking} />}
          </div>

          {/* Sidebar - right (desktop) */}
          <div className="hidden lg:block lg:col-span-3 order-1 lg:order-2">
            <Card className="bg-zinc-900 border-zinc-800 sticky top-24">
              <CardBody className="p-0">
                <div className="px-4 py-3 border-b border-zinc-800">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-400">
                    Alle Ranglisten
                  </h3>
                </div>
                <ul className="divide-y divide-zinc-800 max-h-[70vh] overflow-y-auto">
                  {rankings.map((r) => {
                    const id = (r.id || r._id) as string;
                    const isSelected = id === selectedRankingId;
                    return (
                      <li key={id}>
                        <button
                          onClick={() => selectRanking(id)}
                          className={`w-full text-left px-4 py-3 transition-colors ${
                            isSelected ? 'bg-zinc-800/80' : 'hover:bg-zinc-800/40'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex-shrink-0 w-1 h-8 rounded-full"
                              style={{ backgroundColor: isSelected ? TEAL : 'transparent' }}
                            />
                            <div className="flex-1 min-w-0">
                              <p
                                className={`text-sm font-medium line-clamp-1 ${
                                  isSelected ? 'text-white' : 'text-gray-300'
                                }`}
                              >
                                {r.name || r.shortName || 'Rangliste'}
                              </p>
                              {r.organization && (
                                <p className="text-xs text-gray-500 line-clamp-1">
                                  {r.organization}
                                </p>
                              )}
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------- Ranking Hero (großes Banner wie PDC-Turnier-Detailseite) ----------
interface RankingHeroProps {
  ranking: ProRanking;
}

const RankingHero: React.FC<RankingHeroProps> = ({ ranking }) => {
  const bannerUrl = proRankingService.buildFileUrl(
    ranking.heroImage || ranking.thumbnail || ranking.image || null,
  );

  return (
    <div className="relative mb-4 overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900">
      {/* Banner-Hintergrund mit dunklem Overlay (Lesbarkeit wie PDC-Detail) */}
      {bannerUrl && (
        <>
          <img
            src={bannerUrl}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.72) 55%, rgba(0,0,0,0.45) 100%)',
            }}
          />
        </>
      )}

      <div className={`relative p-5 sm:p-7 ${bannerUrl ? 'min-h-[200px] flex flex-col justify-end' : ''}`}>
        <div className="flex flex-wrap items-center gap-2 mb-2">
          {ranking.organization && (
            <Chip size="sm" variant="flat" style={{ backgroundColor: `${TEAL}33`, color: TEAL }}>
              {ranking.organization}
            </Chip>
          )}
          <Chip size="sm" variant="flat" className="bg-zinc-800/80 text-gray-300">
            Offizielle Rangliste
          </Chip>
          {ranking.sponsor && (
            <Chip size="sm" variant="flat" className="bg-zinc-800/80 text-gray-300">
              präsentiert von {ranking.sponsor}
            </Chip>
          )}
        </div>

        <h2 className="text-2xl md:text-4xl font-extrabold tracking-tight text-white">
          {ranking.name || ranking.shortName || 'Rangliste'}
        </h2>

        {(ranking.intro || ranking.description) && (
          <p className="mt-2 max-w-3xl text-sm md:text-base text-gray-300">
            {ranking.intro || ranking.description}
          </p>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-400">
          {ranking.dataAsOf && (
            <span className="inline-flex items-center gap-1.5">
              <Sparkles size={13} style={{ color: TEAL }} />
              {ranking.dataAsOf}
            </span>
          )}
          {typeof ranking.entryCount === 'number' && ranking.entryCount > 0 && (
            <span>{ranking.entryCount} Spieler</span>
          )}
          {ranking.valueLabel && <span>Wertung: {ranking.valueLabel}</span>}
          {ranking.officialWebsite && (
            <a
              href={ranking.officialWebsite}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 underline hover:text-white"
              style={{ color: TEAL }}
            >
              Offizielle Website <ExternalLink size={12} />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------- Key Facts (Eckdaten-Karten wie PDC-Detailseite) ----------
const RankingKeyFacts: React.FC<{ ranking: ProRanking }> = ({ ranking }) => {
  const facts = (ranking.keyFacts ?? [])
    .filter((f) => f && f.label && f.value)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  if (facts.length === 0) return null;

  return (
    <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
      {facts.slice(0, 8).map((fact, idx) => (
        <Card key={`${fact.label}-${idx}`} className="bg-zinc-900 border-zinc-800">
          <CardBody className="p-3.5">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              {fact.label}
            </p>
            <p className="mt-1 text-sm md:text-base font-bold text-white">{fact.value}</p>
            {fact.sublabel && (
              <p className="mt-0.5 text-[11px] text-gray-500">{fact.sublabel}</p>
            )}
          </CardBody>
        </Card>
      ))}
    </div>
  );
};

// ---------- Info-Sektionen (Übersicht, Funktionsweise, Historie, Rekorde) ----------
const RankingInfoSections: React.FC<{ ranking: ProRanking }> = ({ ranking }) => {
  const pastLeaders = (ranking.pastLeaders ?? [])
    .filter((l) => l && typeof l.year === 'number')
    .sort((a, b) => (b.year ?? 0) - (a.year ?? 0));
  const records = (ranking.records ?? []).filter((r) => r && r.label && r.value);
  const hasAny = ranking.overview || ranking.howItWorks || pastLeaders.length > 0 || records.length > 0;
  if (!hasAny) return null;

  return (
    <div className="mt-6 space-y-4">
      {ranking.overview && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardBody className="p-5">
            <h3 className="mb-3 flex items-center gap-2 text-lg font-bold text-white">
              <BookOpen size={18} style={{ color: TEAL }} />
              Über die Rangliste
            </h3>
            <div className="space-y-3 text-sm leading-relaxed text-gray-300">
              {ranking.overview.split(/\n{2,}|\n/).filter((p) => p.trim()).map((paragraph, idx) => (
                <p key={idx}>{paragraph}</p>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {ranking.howItWorks && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardBody className="p-5">
            <h3 className="mb-3 flex items-center gap-2 text-lg font-bold text-white">
              <HelpCircle size={18} style={{ color: TEAL }} />
              So funktioniert die Rangliste
            </h3>
            <div className="space-y-3 text-sm leading-relaxed text-gray-300">
              {ranking.howItWorks.split(/\n{2,}|\n/).filter((p) => p.trim()).map((paragraph, idx) => (
                <p key={idx}>{paragraph}</p>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {pastLeaders.length > 0 && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardBody className="p-0">
            <div className="border-b border-zinc-800 px-5 py-4">
              <h3 className="flex items-center gap-2 text-lg font-bold text-white">
                <Crown size={18} style={{ color: '#FFD700' }} />
                Historie: Die Nummer 1
              </h3>
            </div>
            <ul className="divide-y divide-zinc-800">
              {pastLeaders.map((leader, idx) => (
                <li key={`${leader.year}-${idx}`} className="flex items-center gap-4 px-5 py-3">
                  <span className="w-12 shrink-0 text-sm font-bold" style={{ color: TEAL }}>
                    {leader.year}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-white">{leader.playerName || '–'}</p>
                    {leader.note && <p className="text-xs text-gray-500">{leader.note}</p>}
                  </div>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      {records.length > 0 && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardBody className="p-0">
            <div className="border-b border-zinc-800 px-5 py-4">
              <h3 className="flex items-center gap-2 text-lg font-bold text-white">
                <Trophy size={18} style={{ color: TEAL }} />
                Rekorde
              </h3>
            </div>
            <ul className="divide-y divide-zinc-800">
              {records.map((record, idx) => (
                <li key={`${record.label}-${idx}`} className="flex items-center justify-between gap-4 px-5 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-white">{record.label}</p>
                    {(record.holder || record.year || record.note) && (
                      <p className="text-xs text-gray-500">
                        {[record.holder, record.year, record.note].filter(Boolean).join(' · ')}
                      </p>
                    )}
                  </div>
                  <span className="shrink-0 text-sm font-bold" style={{ color: TEAL }}>
                    {record.value}
                  </span>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}
    </div>
  );
};

// ---------- Entries Table ----------
interface EntriesTableProps {
  ranking: ProRankingEntriesResponse['ranking'] | null;
  entries: ProRankingEntry[];
  onLoadMore: () => void;
  hasMore: boolean;
  loadingMore: boolean;
  remaining: number;
}

const EntriesTable: React.FC<EntriesTableProps> = ({
  ranking,
  entries,
  onLoadMore,
  hasMore,
  loadingMore,
  remaining,
}) => {
  const valueColumnHeader = ranking?.valueLabel || 'Wert';
  const multiplierHint = ranking?.valueMultiplierHint;

  if (entries.length === 0) {
    return (
      <Card className="bg-zinc-900 border-zinc-800">
        <CardBody className="p-12 text-center">
          <BarChart3 size={48} className="mx-auto text-gray-600 mb-3" />
          <p className="text-gray-400">Keine Einträge in dieser Rangliste</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardBody className="p-0">
        {multiplierHint && (
          <div className="px-4 py-3 border-b border-zinc-800 bg-zinc-900/60 flex items-start gap-2">
            <Info size={16} className="text-gray-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-gray-400">{multiplierHint}</p>
          </div>
        )}

        {/* Header row */}
        <div className="grid grid-cols-12 px-4 py-3 border-b border-zinc-800 text-xs uppercase tracking-wide text-gray-500 font-semibold">
          <div className="col-span-2 md:col-span-1">Rang</div>
          <div className="col-span-5 md:col-span-7">Spieler</div>
          <div className="col-span-1 text-center">+/-</div>
          <div className="col-span-4 md:col-span-3 text-right">{valueColumnHeader}</div>
        </div>

        {/* Rows */}
        <ul className="divide-y divide-zinc-800">
          {entries.map((entry, idx) => (
            <EntryRow key={entry.id || entry._id || idx} entry={entry} ranking={ranking} />
          ))}
        </ul>

        {hasMore && (
          <div className="p-4 border-t border-zinc-800 flex justify-center">
            <Button
              onPress={onLoadMore}
              isDisabled={loadingMore}
              isLoading={loadingMore}
              variant="flat"
              className="bg-zinc-800 text-white"
            >
              {loadingMore
                ? 'Lädt...'
                : `Weitere ${Math.min(PAGE_SIZE, remaining)} Spieler laden`}
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

// ---------- Entry Row ----------
interface EntryRowProps {
  entry: ProRankingEntry;
  ranking: ProRankingEntriesResponse['ranking'] | null;
}

const EntryRow: React.FC<EntryRowProps> = ({ entry, ranking }) => {
  const rank = entry.rank ?? 0;
  const isTop10 = rank > 0 && rank <= 10;
  const rowBg = isTop10
    ? rank <= 3
      ? 'bg-[#3FCED2]/[0.06]'
      : 'bg-[#3FCED2]/[0.03]'
    : '';

  return (
    <li className={`grid grid-cols-12 px-4 py-3 items-center ${rowBg}`}>
      <div className="col-span-2 md:col-span-1">
        <RankBadge rank={rank} />
      </div>
      <div className="col-span-5 md:col-span-7 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          {entry.country && (
            <span
              className="inline-block w-5 text-center text-[10px] font-bold uppercase text-gray-400 flex-shrink-0"
              title={entry.country}
            >
              {entry.country}
            </span>
          )}
          <span
            className={`truncate ${
              isTop10 ? 'font-semibold text-white' : 'text-gray-200'
            }`}
          >
            {entry.playerName || 'Unbekannt'}
          </span>
        </div>
      </div>
      <div className="col-span-1 flex justify-center">
        <TrendBadge change={entry.rankChange} previousRank={entry.previousRank} rank={rank} />
      </div>
      <div className="col-span-4 md:col-span-3 text-right tabular-nums">
        <span className={isTop10 ? 'font-semibold text-white' : 'text-gray-300'}>
          {proRankingService.formatValue(entry, ranking)}
        </span>
      </div>
    </li>
  );
};

// ---------- Trend Badge (Auf-/Abstieg wie dartsrankings.com) ----------
const TrendBadge: React.FC<{
  change?: number | null;
  previousRank?: number | null;
  rank: number;
}> = ({ change, previousRank, rank }) => {
  // rankChange bevorzugen; sonst aus previousRank ableiten
  let delta = typeof change === 'number' && Number.isFinite(change) ? change : null;
  if (delta == null && typeof previousRank === 'number' && previousRank > 0 && rank > 0) {
    delta = previousRank - rank;
  }
  if (delta == null) {
    return <span className="text-xs text-zinc-700">–</span>;
  }
  if (delta > 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-emerald-400">
        <ArrowUp size={12} />{delta}
      </span>
    );
  }
  if (delta < 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-red-400">
        <ArrowDown size={12} />{Math.abs(delta)}
      </span>
    );
  }
  return <Minus size={12} className="text-zinc-600" />;
};

// ---------- Rank Badge ----------
const RankBadge: React.FC<{ rank: number }> = ({ rank }) => {
  if (rank === 1) {
    return (
      <div className="flex items-center gap-1">
        <Trophy size={16} style={{ color: '#FFD700' }} />
        <span className="text-sm font-bold" style={{ color: '#FFD700' }}>
          {rank}
        </span>
      </div>
    );
  }
  if (rank === 2) {
    return (
      <div className="flex items-center gap-1">
        <Award size={16} style={{ color: '#C0C0C0' }} />
        <span className="text-sm font-bold" style={{ color: '#C0C0C0' }}>
          {rank}
        </span>
      </div>
    );
  }
  if (rank === 3) {
    return (
      <div className="flex items-center gap-1">
        <Medal size={16} style={{ color: '#CD7F32' }} />
        <span className="text-sm font-bold" style={{ color: '#CD7F32' }}>
          {rank}
        </span>
      </div>
    );
  }
  return <span className="text-sm text-gray-400 font-medium">{rank || '-'}</span>;
};

export default RankingsPage;
